import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ShieldAlert, Home } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { shouldRequireAuth } from '@/lib/auth-utils'

type Role = 'student' | 'teacher' | 'admin'

interface RoleGuardProps {
  roles: Role[]
  children: ReactNode
}

/**
 * 角色守衛：僅允許指定角色進入。
 * 注意：這只是 UX 層防護，真正的權限控管在 API 端（adminOnly / teacherOrAdmin middleware）。
 */
export function RoleGuard({ roles, children }: RoleGuardProps) {
  const user = useAuthStore((state) => state.user)

  // 開發模式（不要求登入）時不擋角色，方便本地開發
  if (!shouldRequireAuth()) {
    return <>{children}</>
  }

  const role = user?.role
  if (role && roles.includes(role)) {
    return <>{children}</>
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6 text-center space-y-4">
          <ShieldAlert className="w-12 h-12 text-error mx-auto" />
          <h1 className="text-xl font-semibold text-foreground">權限不足</h1>
          <p className="text-sm text-muted-foreground">
            此頁面僅限{roles.includes('admin') ? '管理員' : '特定角色'}使用。
            {role ? `您目前的角色為「${role}」。` : '請先登入。'}
          </p>
          <Button asChild className="flex items-center gap-2 mx-auto w-fit">
            <Link to="/">
              <Home className="w-4 h-4" />
              回到首頁
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
